import { useEffect, useState } from "react";
import { Award, BookOpen, CheckCircle2, Clock, MessageSquare, PlayCircle, ArrowRight, Megaphone } from "lucide-react";
import { downloadBlob } from "@/components/learn/ResourceList";

const Stat = ({ icon: Icon, value, label, testId }) => (
  <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur" data-testid={testId}>
    <Icon size={16} className="text-sky-300" />
    <p className="mt-1 font-heading text-2xl font-bold text-white">{value}</p>
    <p className="text-[11px] text-sky-100/80">{label}</p>
  </div>
);

export const CertificateButton = ({ api, course, compact }) => course.progress_pct === 100 && course.lesson_count > 0 ? (
  <button onClick={(e) => { e.stopPropagation(); downloadBlob(api, `/learner/courses/${course.id}/certificate`, "certificate.pdf"); }}
    className={`inline-flex items-center gap-1.5 rounded-lg bg-amber-400 font-semibold text-slate-900 hover:bg-amber-300 transition-colors ${compact ? "px-2.5 py-1 text-[11px]" : "px-3 py-2 text-xs"}`} data-testid={`certificate-${course.id}`}>
    <Award size={compact ? 12 : 14} /> Certificate
  </button>
) : null;

const CourseCard = ({ api, c, onOpen }) => (
  <button onClick={() => onOpen(c)} className="group text-left rounded-xl border border-border bg-white p-5 transition-[transform,box-shadow,border-color] hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-lg" data-testid={`course-${c.id}`}>
    <span className="inline-block rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700">{c.award}</span>
    <h3 className="mt-2 font-heading text-lg font-semibold text-slate-900">{c.title}</h3>
    {c.summary && <p className="mt-1 line-clamp-2 text-xs text-slate-500">{c.summary}</p>}
    <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-slate-100" data-testid={`course-progress-${c.id}`}>
      <div className="h-full rounded-full bg-gradient-to-r from-blue-600 to-sky-400 transition-[width] duration-700" style={{ width: `${c.progress_pct || 0}%` }} />
    </div>
    <div className="mt-2 flex items-center justify-between">
      <p className="text-[11px] text-slate-500">{c.completed_lessons || 0} of {c.lesson_count} lessons · {c.progress_pct || 0}%</p>
      <CertificateButton api={api} course={c} compact />
    </div>
  </button>
);

export const LearnerDashboard = ({ api, courses, onOpenCourse, onResume }) => {
  const [d, setD] = useState(null);
  const [news, setNews] = useState([]);
  useEffect(() => {
    api.get("/learner/dashboard").then(({ data }) => setD(data)).catch(() => setD({}));
    api.get("/learner/announcements").then(({ data }) => setNews(data.announcements || [])).catch(() => {});
  }, [api]);
  const first = (d?.name || "").split(" ")[0];

  return (
    <div className="space-y-8" data-testid="learner-dashboard">
      <section className="relative overflow-hidden rounded-2xl bg-[#06142e] p-6 sm:p-8 text-white auth-rise">
        <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-blue-600/30 blur-3xl" />
        <div className="relative grid gap-6 lg:grid-cols-[1fr_auto] lg:items-end">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-sky-300">Learner portal</p>
            <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-bold tracking-tight" data-testid="dashboard-welcome">Welcome back{first ? `, ${first}` : ""}</h1>
            <div className="mt-5 max-w-md">
              <div className="flex justify-between text-xs text-sky-100/80"><span>Overall progress</span><span data-testid="dashboard-overall">{d?.overall_progress_pct ?? 0}%</span></div>
              <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-white/15"><div className="h-full rounded-full bg-gradient-to-r from-sky-400 to-blue-500 transition-[width] duration-700" style={{ width: `${d?.overall_progress_pct ?? 0}%` }} /></div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Stat icon={CheckCircle2} value={`${d?.lessons_completed ?? 0}/${d?.lessons_total ?? 0}`} label="Lessons done" testId="dashboard-stat-lessons" />
            <Stat icon={Clock} value={d?.awaiting_marking ?? 0} label="Awaiting marking" testId="dashboard-stat-awaiting" />
            <Stat icon={Award} value={d?.certificates ?? 0} label="Certificates" testId="dashboard-stat-certs" />
          </div>
        </div>
      </section>

      {d?.resume && (
        <button onClick={() => onResume(d.resume)} className="group flex w-full items-center gap-4 rounded-2xl border border-blue-200 bg-gradient-to-r from-blue-50 to-white p-5 text-left transition-shadow hover:shadow-md" data-testid="dashboard-resume">
          <PlayCircle size={40} className="shrink-0 text-blue-600" />
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-700">Continue where you left off</p>
            <p className="truncate font-heading text-lg font-semibold text-slate-900">{d.resume.lesson_title}</p>
            <p className="text-xs text-slate-500">{d.resume.course_title} · Lesson {d.resume.lesson_number} of {d.resume.lesson_count}</p>
          </div>
          <ArrowRight className="text-blue-600 transition-transform group-hover:translate-x-1" />
        </button>
      )}

      <section className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-3">
          <h2 className="flex items-center gap-2 font-heading text-lg font-semibold"><BookOpen size={18} /> Your courses</h2>
          <div className="grid gap-3 sm:grid-cols-2">{courses.map((c) => <CourseCard key={c.id} api={api} c={c} onOpen={onOpenCourse} />)}</div>
          {courses.length === 0 && <p className="text-sm text-slate-500">You're not enrolled on any courses yet. If this looks wrong, email your training coordinator.</p>}
        </div>
        <aside className="space-y-3" data-testid="dashboard-feedback">
          {news.length > 0 && (
            <div className="space-y-2" data-testid="dashboard-announcements">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold"><Megaphone size={18} /> Announcements</h2>
              {news.slice(0, 4).map((a) => (
                <div key={a.id} className="rounded-xl border-l-4 border-blue-600 bg-white p-4 shadow-sm" data-testid={`dashboard-announcement-${a.id}`}>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-blue-700">{a.course_title}</p>
                  <p className="mt-0.5 text-sm font-semibold text-slate-900">{a.title}</p>
                  <p className="mt-1 whitespace-pre-wrap text-xs text-slate-600 line-clamp-5">{a.body}</p>
                  <p className="mt-2 text-[10px] text-slate-400">{a.author_name} · {new Date(a.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          )}
          <h2 className="flex items-center gap-2 font-heading text-lg font-semibold"><MessageSquare size={18} /> Tutor feedback</h2>
          {(d?.recent_feedback || []).map((f) => (
            <div key={f.attempt_id} className="rounded-xl border border-border bg-white p-4">
              <p className="text-xs font-semibold text-slate-900">{f.lesson_title} <span className={f.passed ? "text-green-700" : "text-amber-600"}>· {f.score_pct}%</span></p>
              <p className="mt-1.5 line-clamp-4 text-xs text-slate-600">"{f.feedback}"</p>
            </div>
          ))}
          {d && !(d.recent_feedback || []).length && <p className="rounded-xl border border-dashed border-border p-4 text-xs text-slate-500">Feedback from your tutor will appear here once your written answers are marked.</p>}
        </aside>
      </section>
    </div>
  );
};
